import { gql, useQuery, useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import { captureException } from "@sentry/react";
import { isEmail } from "validator";

import { getFirstSixDigits } from "modules/functions";
import LoadingBox from "components/other/LoadingBox";
import BoxLoadingSpinner from "components/other/BoxLoadingSpinner";
import MailingListEmailForm from "./MailingListEmailForm";

export default ({ email, openErrorWindow, changeEmail, closeEmailForm }) => {
  const [emailSelectionOpened, setEmailSelectionOpened] = useState(false);
  const [emailSelected, setEmailSelected] = useState(false);
  const [newEmailFormOpened, setNewEmailFormOpened] = useState(false);
  const [emailWarning, setEmailWarning] = useState(false);
  const [loadingAddEmail, setLoadingAddEmail] = useState(false);
  const [emailInput, setEmailInput] = useState("");
  const [errorMessage, setErrorMessage] = useState(null);
  const [codeInputVisible, setCodeInputVisible] = useState(false);
  const [codeInput, setCodeInput] = useState("");
  const [loadingVerifyCode, setLoadingVerifyCode] = useState(false);
  const [emailToVerify, setEmailToVerify] = useState("");
  const [codeWarning, setCodeWarning] = useState(false);

  // Fetch data
  const GET_EMAIL_FORM_DATA = gql`
    query GetMailingListEmailFormData {
      mailingListEmailFormEmails
    }
  `;

  const { data, error, refetch } = useQuery(GET_EMAIL_FORM_DATA, {
    fetchPolicy: "network-only",
  });

  if (error) {
    console.log("error fetching email form data", error);
    captureException(error);
  }

  useEffect(() => {
    if (email) {
      setEmailSelected(email);
    }
  }, [email]);

  const onEmailSelect = (email) => {
    setEmailSelected(email);
  };

  // handle Add new email
  const toggleEmailSelection = () => {
    setEmailSelectionOpened((prevState) => !prevState);
  };

  const togglenNewEmailForm = () => {
    setEmailSelectionOpened(false);
    setNewEmailFormOpened((prevState) => !prevState);
    setCodeInputVisible(false);
    setEmailWarning(false);
    setErrorMessage(null);
  };

  const handleEmailInputChange = (e) => {
    setEmailInput(e.target.value);
  };

  const ADD_VERIFIED_EMAIL = gql`
    mutation AddVerifiedEmail($email: String!) {
      addListVerifiedEmail(email: $email) {
        resultCode
      }
    }
  `;

  const [addListVerifiedEmail] = useMutation(ADD_VERIFIED_EMAIL);

  const addNewEmail = async () => {
    try {
      if (loadingAddEmail) return;

      setErrorMessage(null);
      setEmailWarning(false);

      //validate email
      if (
        emailInput.trim().length < 5 ||
        emailInput.trim().length > 100 ||
        !isEmail(emailInput)
      ) {
        setEmailWarning(true);
        return;
      }

      setLoadingAddEmail(true);
      setEmailToVerify("");

      const result = await addListVerifiedEmail({
        variables: { email: emailInput },
      });

      const { resultCode } = result.data.addListVerifiedEmail;

      console.log("resultCode", resultCode);

      if (resultCode === 5) {
        setTimeout(
          () => setErrorMessage("This email address has already been added."),
          300
        );

        setLoadingAddEmail(false);
        return;
      }

      if (resultCode !== 0) {
        setLoadingAddEmail(false);
        openErrorWindow();
        return;
      }

      setEmailToVerify(emailInput);
      setEmailInput("");
      setNewEmailFormOpened(false);
      setCodeInputVisible(true);

      setLoadingAddEmail(false);
    } catch (error) {
      console.log("addNewEmail error", error);
      captureException(error);
      setLoadingAddEmail(false);
      openErrorWindow();
      return;
    }
  };

  const handleCodeInputChange = (e) => {
    setCodeInput(e.target.value);
  };

  const VERIFY_LIST_EMAIL = gql`
    mutation VerifyListEmail($email: String!, $code: String!) {
      verifyListEmail(email: $email, code: $code) {
        resultCode
      }
    }
  `;

  const [verifyListEmail] = useMutation(VERIFY_LIST_EMAIL);

  const verifyCode = async () => {
    try {
      setCodeWarning(false);

      if (loadingVerifyCode) return;
      if (codeInput.length === 0) {
        setCodeWarning(true);
        return;
      }
      if (emailToVerify === "") return;

      setErrorMessage(null);

      const cleanCode = getFirstSixDigits(codeInput);

      setLoadingVerifyCode(true);

      console.log({ emailToVerify, cleanCode });

      const result = await verifyListEmail({
        variables: { email: emailToVerify, code: cleanCode },
      });

      const { resultCode } = result.data.verifyListEmail;

      console.log("resultCode", resultCode);

      if (resultCode === 4 || resultCode === 6) {
        setTimeout(
          () =>
            setErrorMessage("The verification code you entered is incorrect."),
          300
        );

        setLoadingVerifyCode(false);
        return;
      }

      if (resultCode !== 0) {
        setLoadingVerifyCode(false);
        openErrorWindow();
        return;
      }

      setEmailToVerify("");
      setCodeInputVisible(false);
      setLoadingVerifyCode(false);
      await refetch();
      setEmailSelected(emailToVerify);
    } catch (error) {
      console.log("verifyCode error", error);
      captureException(error);
      setLoadingVerifyCode(false);
      openErrorWindow();
      return;
    }
  };

  const selectEmail = () => {
    changeEmail(emailSelected);
    closeEmailForm();
  };

  console.log("data", data);

  return (
    <>
      {!data && (
        <LoadingBox>
          <BoxLoadingSpinner height="325px" />
        </LoadingBox>
      )}
      {data && (
        <MailingListEmailForm
          emailSelected={emailSelected}
          data={data}
          emailSelectionOpened={emailSelectionOpened}
          toggleEmailSelection={toggleEmailSelection}
          onEmailSelect={onEmailSelect}
          togglenNewEmailForm={togglenNewEmailForm}
          newEmailFormOpened={newEmailFormOpened}
          emailWarning={emailWarning}
          loadingAddEmail={loadingAddEmail}
          emailInput={emailInput}
          handleEmailInputChange={handleEmailInputChange}
          addNewEmail={addNewEmail}
          errorMessage={errorMessage}
          codeInputVisible={codeInputVisible}
          codeInput={codeInput}
          handleCodeInputChange={handleCodeInputChange}
          loadingVerifyCode={loadingVerifyCode}
          verifyCode={verifyCode}
          codeWarning={codeWarning}
          selectEmail={selectEmail}
        />
      )}
    </>
  );
};
